/* eslint-disable no-restricted-globals */

// This service worker can be customized!
// See https://developers.google.com/web/tools/workbox/modules
// for the list of available Workbox modules, or add any other
// code you'd like.
// You can also remove this file if you'd prefer not to use a
// service worker, and the Workbox build step will be skipped.

import { clientsClaim } from 'workbox-core'
import { ExpirationPlugin } from 'workbox-expiration'
import { registerRoute } from 'workbox-routing'
import { NetworkFirst } from 'workbox-strategies'

// TTL set to 60 minutes
const CACHE_MAX_AGE = 3600 // in seconds
export const STATIC_CACHE_NAME = 'vn-static-v1'
export const DYNAMIC_CACHE_NAME = 'vn-get-requests-v1'
export const POST_CACHE_NAME = 'vn-post-requests-v1'
export const MESSAGE_PRECACHE = 'INITALIZE_PRECACHE'

/**
 * Claim any currently available clients once the service worker
 * becomes active. This is normally used in conjunction with `skipWaiting()`.
 */
clientsClaim()

// Precache all of the assets generated by your build process.
const preCacheExtensions = ['js', 'html', 'css', 'woff2']
const landingRoutes = [
  '/',
  '?apk=true&cfd=false',
  '/?apk=true&cfd=false', // backwards compatibility
  '?apk=true&cfd=false&eDCC=false',
  '/concession-order?=user-login',
  '/concession-order',
  '/orders',
  '/device-login',
  '/register-device',
  '/revenue-center-setup',
  '/user-login',
]

// eslint-disable-next-line no-unused-vars
const WB_MANIFEST = self.__WB_MANIFEST

/**
 * PreCache entry points routes, bundle, index, fonts and stylesheets
 * Choosing to use custom precache.addAll instead of manifect precache because of revisions
 */
const initializePreCache = async () => {
  const response = await fetch('/asset-manifest.json')
  const assets = await response.json()

  const urlsToCache = Object.values(assets.files).filter((path) => {
    if (path.includes('service-worker.js')) {
      return false
    }

    const extension = path.split('.').pop()
    return preCacheExtensions.includes(extension)
  })

  const preCache = await caches.open(STATIC_CACHE_NAME)
  return preCache.addAll([...urlsToCache, ...landingRoutes])
}
self.addEventListener('install', async (event) => {
  event.waitUntil(initializePreCache())
})

/**
 * Listen to messages from web application
 */
self.addEventListener('message', (event) => {
  switch (event?.data?.type) {
    case MESSAGE_PRECACHE:
      initializePreCache()
      break
    default:
      break
  }
})

/**
 * Upon "Sync Network Data", we invalidate all caches
 * this is a fallback rules for registering static files
 */
const cachedTypes = ['document', 'script', 'image', 'style']
const isRegisteredRouteByType = (request, type) => {
  // used for checking if device has a valid network connectivity
  if (request.url.endsWith('blank.png')) {
    return false
  }

  // used for checking current app version
  if (request.url.endsWith('version.txt')) {
    return false
  }

  // prevents this file from being cached
  if (request.url.endsWith('service-worker.js')) {
    return false
  }

  if (request.destination !== type) {
    return false
  }

  return true
}

// Register routes
cachedTypes.forEach((type) => {
  registerRoute(
    ({ request }) => isRegisteredRouteByType(request, type),
    new NetworkFirst({
      cacheName: STATIC_CACHE_NAME,
      plugins: [new ExpirationPlugin({ maxAgeSeconds: CACHE_MAX_AGE })],
    }),
  )
})

// NetworkFirst policies to allow for temporary offline capabilities
const cachedRoutes = [
  '/api/v1/attendant_settings',
  '/devices/login',
  '/api/v1/app_device_configurations',
  '/operator/attendants',
  '/operator/employee_roles',
  '/ppi/configurations',
  '/pos/stands',
  '/pos/printers',
  '/pos/promotions',
  '/pos/menus',
  '/operator/employee_roles',
]

cachedRoutes.forEach((route) => {
  registerRoute(
    ({ request }) => request.url.includes(route),
    new NetworkFirst({
      cacheName: DYNAMIC_CACHE_NAME,
      plugins: [new ExpirationPlugin({ maxAgeSeconds: CACHE_MAX_AGE })],
    }),
  )
})

const postRoutes = ['/devices/login']

// Function to generate a unique cache key for POST requests
const generateCacheKey = async (request) => {
  const requestClone = request.clone()
  const body = await requestClone.text()
  return request.url + '|' + body
}

// Function to handle POST requests and cache their responses
const handlePostRequest = async ({ request }) => {
  const cache = await caches.open(POST_CACHE_NAME)
  const cacheKey = await generateCacheKey(request)
  const cachedResponse = await cache.match(cacheKey)
  const now = Date.now()
  const cachedDate = cachedResponse?.headers?.get('date')
  const date = cachedDate ? new Date(cachedDate).getTime() : now
  const ageInSeconds = (now - date) / 1000
  const isExpired = ageInSeconds > CACHE_MAX_AGE

  let reponseError
  try {
    const fetchResponse = await fetch(request.clone())
    if (fetchResponse?.status === 200) {
      await cache.put(cacheKey, fetchResponse.clone())
      return fetchResponse
    }
  } catch (e) {
    reponseError = e
  }

  if (!cachedResponse || isExpired) {
    return reponseError
  }

  return cachedResponse
}

postRoutes.forEach((route) => {
  registerRoute(({ url, request }) => request.method === 'POST' && url.pathname.startsWith(route), handlePostRequest, 'POST')
})

// Network First, fallback on Cache
const respondNetworkFirst = async (event) => {
  try {
    return await fetch(event.request)
  } catch (err) {
    // add a feature flag to serve or not
    const response = await caches.match(event.request)

    return response
  }
}

// main intercepting function - Network first
self.addEventListener('fetch', (event) => {
  event.respondWith(respondNetworkFirst(event))
})
